import { useCallback } from 'react'

import { getTracking } from '@vp/tracking'
import type { DynamicSizedQuantities } from '@vp/lat-react-component-library'
import { useLogger } from '@vp/ubik-context'

import { TrackingCategory, TrackingAction, TrackingLabel } from './TrackingConstants'
import type { TrackingProductInput } from '../../../../types/Tracking'
import type { Quantity } from '../../../../types/Inventory'
import { useAppState } from '../AppContext'
import { useTenant } from '../TenantContext'

export type IUseTracking = {
  trackAddToCart: (
    label: TrackingLabel,
    productKey: string,
    productVersion: number,
    selectedAttributes: Record<string, string>,
    mpvId: string,
    name: string,
    quantity: Quantity,
  ) => void
  trackProductViewed: (label: TrackingLabel, mpvId: string, name: string, productKey: string, productVersion: number) => void
  trackProductListViewed: (label: TrackingLabel, products: TrackingProductInput[]) => void
  trackProductRemoved: (label: TrackingLabel, mpvId: string, name: string, productKey: string, productVersion: number) => void
  trackSizeChartOpened: (label: TrackingLabel, mpvId: string, name: string, productKey: string, productVersion: number) => void
  trackSizeChartClosed: (label: TrackingLabel, mpvId: string, name: string, productKey: string, productVersion: number) => void
  trackProductUndo: (label: TrackingLabel, mpvId: string, name: string, productKey: string, productVersion: number) => void
  trackProductAdded: (label: TrackingLabel, mpvId: string, name: string, productKey: string, productVersion: number, position: number) => void
  trackMatchingProductAddToCartError: (label: TrackingLabel, mpvId: string, name: string, productKey: string, productVersion: number) => void
  trackExperimentClickedEvent: (
    navigationDetail: string,
    experimentName: string,
    variationName: string,
    mpvId: string,
    name: string,
    productKey: string,
    productVersion: number,
    eventDetail?: string,
    experimentDetail?: string,
  ) => void
}

export const useTracking = (): IUseTracking => {
  const { primaryProject } = useAppState()
  const { tenant } = useTenant()
  const logger = useLogger()
  const pageDept = primaryProject?.pageMode === 'Sized Good' ? 'PPAGSG' : 'PPAGHG'

  const trackEvent = useCallback((eventType: TrackingAction, properties: object) => {
    const tracking = getTracking()
    if (tracking) {
      tracking.track(eventType, properties)
    } else {
      logger.warn(`Attempted to track ${eventType} event, but the react-tracking library is unavailable.`)
    }
  }, [])

  const trackEventWithoutCategory = useCallback(
    (action: TrackingAction, label: TrackingLabel, mpvId: string, name: string, productKey: string, productVersion: number) => {
      const properties = {
        label,
        core_product_id: productKey,
        core_product_version: productVersion,
        product_id: mpvId,
        name,
        pageDept,
      }

      trackEvent(action, properties)
    },
    [trackEvent, pageDept],
  )

  const trackEventWithCategory = useCallback(
    (
      action: TrackingAction,
      category: TrackingCategory,
      label: TrackingLabel,
      mpvId: string,
      name: string,
      productKey: string,
      productVersion: number,
      productPosition?: number,
      pricingAndSelectedOptions?: object,
    ) => {
      let properties = {
        category,
        label,
        core_product_id: productKey,
        core_product_version: productVersion,
        product_id: mpvId,
        name,
        pageDept,
      }

      if (productPosition) {
        properties = { ...properties, ...{ position: productPosition } }
      }

      if (pricingAndSelectedOptions) {
        properties = { ...properties, ...pricingAndSelectedOptions }
      }

      trackEvent(action, properties)
    },
    [trackEvent, pageDept],
  )

  const trackEventForExperiment = useCallback(
    (
      action: TrackingAction,
      navigationDetail: string,
      experimentName: string,
      variationName: string,
      mpvId: string,
      name: string,
      productKey: string,
      productVersion: number,
      eventDetail?: string,
      experimentDetail?: string,
    ) => {
      const properties = {
        label: `Configure - Quantity view`,
        eventDetail,
        navigationDetail,
        trackingTenant: tenant,
        experimentName,
        variationName,
        product_id: mpvId,
        name,
        core_product_id: productKey,
        core_product_version: productVersion,
        experimentDetail,
        pageDept,
      }

      trackEvent(action, properties)
    },
    [trackEvent, pageDept],
  )

  const trackMultiProductEvent = useCallback(
    (action: TrackingAction, label: TrackingLabel, trackingProducts: TrackingProductInput[]) => {
      const products = trackingProducts.map((product: TrackingProductInput, i: number) => {
        return {
          product_id: product.mpvId,
          name: product.productName,
          core_product_id: product.productKey,
          core_product_version: product.productVersion,
          position: i + 1,
        }
      })
      const properties = {
        label,
        products,
      }

      trackEvent(action, properties)
    },
    [trackEvent],
  )

  const trackAddToCart = useCallback(
    (
      label: TrackingLabel,
      productKey: string,
      productVersion: number,
      selectedAttributes: Record<string, string>,
      mpvId: string,
      name: string,
      quantity: Quantity,
    ) => {
      let pricingAndSelectedOptions = {}

      // TODO: Derive pricing data for the tracking event
      if (typeof quantity === 'number') {
        pricingAndSelectedOptions = {
          price: 0,
          list_price: 0,
          quantity: 1,
          sales_quantity: quantity,
          discount: 0,
          variant: JSON.stringify(selectedAttributes),
        }

        trackEventWithCategory(
          TrackingAction.PRODUCT_ADDED,
          TrackingCategory.ENHANCED_ECOMMERCE,
          label,
          mpvId,
          name,
          productKey,
          productVersion,
          undefined,
          pricingAndSelectedOptions,
        )
      } else {
        const sizeQuantities = quantity as DynamicSizedQuantities
        for (const size in sizeQuantities) {
          pricingAndSelectedOptions = {
            price: 0,
            list_price: 0,
            quantity: 1,
            sales_quantity: sizeQuantities[size], // see the sales quantity to the current size's quantity.
            discount: 0,
            variant: JSON.stringify({ ...selectedAttributes, ...{ Size: size } }),
          }

          trackEventWithCategory(
            TrackingAction.PRODUCT_ADDED,
            TrackingCategory.ENHANCED_ECOMMERCE,
            label,
            mpvId,
            name,
            productKey,
            productVersion,
            undefined,
            pricingAndSelectedOptions,
          )
        }
      }
    },
    [trackEventWithCategory],
  )

  const trackProductViewed = useCallback(
    (label: TrackingLabel, mpvId: string, name: string, productKey: string, productVersion: number) => {
      trackEventWithoutCategory(TrackingAction.PRODUCT_VIEWED, label, mpvId, name, productKey, productVersion)
    },
    [trackEventWithoutCategory],
  )

  const trackProductListViewed = useCallback(
    (label: TrackingLabel, products: TrackingProductInput[]) => {
      trackMultiProductEvent(TrackingAction.PRODUCT_LIST_VIEWED, label, products)
    },
    [trackMultiProductEvent],
  )

  const trackProductRemoved = useCallback(
    (label: TrackingLabel, mpvId: string, name: string, productKey: string, productVersion: number) => {
      trackEventWithCategory(TrackingAction.QUANTITY_CLICKED, TrackingCategory.CONFIGURE, label, mpvId, name, productKey, productVersion)
    },
    [trackEventWithCategory],
  )

  const trackSizeChartOpened = useCallback(
    (label: TrackingLabel, mpvId: string, name: string, productKey: string, productVersion: number) => {
      trackEventWithCategory(TrackingAction.FLYOUT_VIEWED, TrackingCategory.FLYOUT, label, mpvId, name, productKey, productVersion)
    },
    [trackEventWithCategory],
  )

  const trackSizeChartClosed = useCallback(
    (label: TrackingLabel, mpvId: string, name: string, productKey: string, productVersion: number) => {
      trackEventWithCategory(TrackingAction.FLYOUT_CLOSED, TrackingCategory.FLYOUT, label, mpvId, name, productKey, productVersion)
    },
    [trackEventWithCategory],
  )

  const trackProductAdded = useCallback(
    (label: TrackingLabel, mpvId: string, name: string, productKey: string, productVersion: number, position: number) => {
      trackEventWithCategory(TrackingAction.QUANTITY_CLICKED, TrackingCategory.CONFIGURE, label, mpvId, name, productKey, productVersion, position)
    },
    [trackEventWithCategory],
  )

  const trackProductUndo = useCallback(
    (label: TrackingLabel, mpvId: string, name: string, productKey: string, productVersion: number) => {
      trackEventWithCategory(TrackingAction.QUANTITY_CLICKED, TrackingCategory.CONFIGURE, label, mpvId, name, productKey, productVersion)
    },
    [trackEventWithCategory],
  )

  const trackMatchingProductAddToCartError = useCallback(
    (label: TrackingLabel, mpvId: string, name: string, productKey: string, productVersion: number) => {
      trackEventWithCategory(TrackingAction.ERROR_DISPLAYED, TrackingCategory.CONFIGURE, label, mpvId, name, productKey, productVersion)
    },
    [trackEventWithCategory],
  )

  const trackExperimentClickedEvent = useCallback(
    (
      navigationDetail: string,
      experimentName: string,
      variationName: string,
      mpvId: string,
      name: string,
      productKey: string,
      productVersion: number,
      eventDetail?: string,
      experimentDetail?: string,
    ) => {
      trackEventForExperiment(
        TrackingAction.EXPERIMENT_CLICKED,
        navigationDetail,
        experimentName,
        variationName,
        mpvId,
        name,
        productKey,
        productVersion,
        eventDetail,
        experimentDetail,
      )
    },
    [trackEventForExperiment],
  )

  return {
    trackAddToCart,
    trackProductViewed,
    trackProductListViewed,
    trackProductRemoved,
    trackSizeChartOpened,
    trackSizeChartClosed,
    trackProductUndo,
    trackProductAdded,
    trackMatchingProductAddToCartError,
    trackExperimentClickedEvent,
  }
}
